import React, { useEffect, useMemo, useState } from "react";
import {
  TWebsiteBuilderSection,
  TWebsiteBuilderSectionChildType,
  WEBSITE_BUILDER_SECTION_CHILD_PLACEMENT_BOUNDARY as SECTION_CHILD_PLACEMENT_BOUNDARY,
  getSectionNextZIndex,
  WEBSITE_BUILDER_SECTION_CLASS_NAME,
} from "../../../../nodes/section/websiteBuilderSection";
import { useWebsiteBuilderContext } from "../../../../websiteBuilderContext";
import classNames from "classnames";
import { IWebsiteBuilderRoot } from "../../../../nodes/websiteBuilderRoot";
import { useTreeRefMapRegistration } from "../../../../hooks/treeRefMap/useTreeRefMapRegistration";
import { useDrop, XYCoord } from "react-dnd";
import {
  getElementPosition,
  isWebsiteBuilderStretchElementData,
  IWebsiteBuilderElementStretch,
  TWebsiteBuilderElement,
} from "../../../../nodes/element/websiteBuilderElement";
import { clamp, cloneDeep } from "lodash";
import { IWebsiteBuilderNodePosition } from "../../../../nodes/websiteBuilderNode";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import {
  websiteBuilderAddNode,
  websiteBuilderSelectNode,
  websiteBuilderUpdateSelectedNode,
} from "redux/actions/websiteBuilder/websiteBuilder";
import { SectionHighlightTitle } from "./SectionHighlightTitle";
import { SectionToolbar } from "./SectionToolbar";
import { SectionAddSectionButtons } from "./SectionAddSectionButtons";
import {
  IUseSectionViewResizeArgs,
  useSectionViewResize,
} from "../../hooks/section/useSectionViewResize/useSectionViewResize";
import { WEBSITE_BUILDER_DESIGN_PAGE_WIDTH } from "../../../WebsiteBuilderDesignSpace";
import { Property } from "csstype";
import {
  IUseSectionViewContextMenuArgs,
  useSectionViewContextMenu,
} from "../../hooks/section/useSectionViewContextMenu/useSectionViewContextMenu";
import { IWebsiteBuilderDesignSpaceBoundaries } from "../../../../WebsiteBuilderEditor";

interface IWebsiteBuilderSectionViewProps {
  parentNode: IWebsiteBuilderRoot;
  node: TWebsiteBuilderSection;
  children: React.ReactNode;
}

interface ISectionViewDropState {
  isOver: boolean;
}

export type TWebsiteBuilderSectionViewHighlight = "none" | "weak" | "strong";

interface ISectionViewOuterPosition {
  type: "outer";
}

interface ISectionViewInnerPosition {
  type: "inner";
  index: number;
}

export type TWebsiteBuilderSectionViewPosition = ISectionViewOuterPosition | ISectionViewInnerPosition;

type TSectionViewDropItemBoundsCheck = "none" | "out-left" | "inside" | "out-right";

const ACCEPTED_SECTION_DROP_CHILDREN: TWebsiteBuilderSectionChildType[] = ["element"];

export function WebsiteBuilderSectionView(props: IWebsiteBuilderSectionViewProps) {
  const { parentNode, node, children } = props;
  const { type: sectionType, height: sectionHeight } = node.section;

  const dispatch = useAppDispatch();

  const { treeRefMap, sectionOperations, clipboard, designSpaceBoundaries, scrollContainerRef } =
    useWebsiteBuilderContext();
  const treeRefMapRegistration = useTreeRefMapRegistration(node.id, treeRefMap);

  const selectedNode = useAppSelector(store => store.websiteBuilderStore.selectedNode);
  const sectionSelected = node.id === selectedNode?.id;
  const childSelected = node.id === selectedNode?.parentId;
  const sectionEditingMode = selectedNode?.editingMode;

  const sectionResizing = sectionSelected && sectionEditingMode === "section-resize";
  const sectionContextMenuActive = sectionSelected && sectionEditingMode === "section-context-menu";

  const [dropItemBoundsCheck, setDropItemBoundsCheck] = useState<TSectionViewDropItemBoundsCheck>("none");

  const [sectionHovered, setSectionHovered] = useState<boolean>(false);

  const sectionMaxIndex = parentNode.children.length - 1;

  const viewResizeArgs: IUseSectionViewResizeArgs = useMemo(
    () => ({
      id: node.id,
      hovered: sectionHovered,
      resizing: sectionResizing,
      height: sectionHeight,
      childrenIds: node.children.map(child => child.id),
      treeRefMap: treeRefMap,
    }),
    [node.id, sectionHovered, sectionResizing, sectionHeight, node.children],
  );

  const { viewResizeHeight, viewResizeHandle } = useSectionViewResize(viewResizeArgs);

  const viewContextMenuArgs: IUseSectionViewContextMenuArgs = useMemo(() => {
    return {
      node,
      parentNode,
      type: sectionType,
      sectionOperations,
      clipboard,
      selected: sectionSelected,
      menuActive: sectionContextMenuActive,
      leftX: designSpaceBoundaries.leftX,
      treeRefMap,
      scrollContainerRef,
    };
  }, [
    node.id,
    parentNode.id,
    sectionType,
    sectionOperations,
    clipboard,
    sectionSelected,
    sectionContextMenuActive,
    designSpaceBoundaries.leftX,
  ]);

  const { viewContextMenu, handleViewOpenContextMenu } = useSectionViewContextMenu(viewContextMenuArgs);

  function clientOffsetBoundsCheck(
    clientOffset: XYCoord,
    sectionBoundingRect: DOMRect,
    designSpaceBoundaries: IWebsiteBuilderDesignSpaceBoundaries,
  ): TSectionViewDropItemBoundsCheck {
    if (!clientOffset || !sectionBoundingRect || !designSpaceBoundaries) {
      return "none";
    } else if (clientOffset.x <= sectionBoundingRect.left + designSpaceBoundaries.leftX) {
      return "out-left";
    } else if (clientOffset.x >= sectionBoundingRect.left + designSpaceBoundaries.rightX) {
      return "out-right";
    } else {
      return "inside";
    }
  }

  const [dropState, drop] = useDrop<TWebsiteBuilderElement, unknown, ISectionViewDropState>(
    () => ({
      accept: ACCEPTED_SECTION_DROP_CHILDREN,
      hover: (item, monitor) => {
        const sectionRef = treeRefMap.current.get(node.id);

        if (!sectionRef) {
          setDropItemBoundsCheck("none");
          return;
        }

        const clientOffset = monitor.getClientOffset();
        const sectionBoundingRect = sectionRef.getBoundingClientRect();

        const updatedDropItemBoundsCheck = clientOffsetBoundsCheck(
          clientOffset,
          sectionBoundingRect,
          designSpaceBoundaries,
        );

        setDropItemBoundsCheck(updatedDropItemBoundsCheck);
      },
      drop: (item, monitor) => {
        const sectionRef = treeRefMap.current.get(node.id);

        if (!sectionRef) {
          return;
        }

        const clientOffset = monitor.getClientOffset();
        const sectionBoundingRect = sectionRef.getBoundingClientRect();

        const boundsCheck = clientOffsetBoundsCheck(clientOffset, sectionBoundingRect, designSpaceBoundaries);

        if (boundsCheck !== "inside") {
          return;
        }

        const elementStretch: IWebsiteBuilderElementStretch = isWebsiteBuilderStretchElementData(item.element)
          ? item.element.stretch
          : null;

        const itemWidth = item.element.dimensions.width;
        const itemHeight = item.element.dimensions.height;

        const screenPosition: IWebsiteBuilderNodePosition = {
          x: clientOffset.x - sectionBoundingRect.left - itemWidth / 2,
          y: clientOffset.y - sectionBoundingRect.top - itemHeight / 2,
        };

        const clampedScreenPosition: IWebsiteBuilderNodePosition = {
          x: clamp(
            screenPosition.x,
            designSpaceBoundaries.leftX + SECTION_CHILD_PLACEMENT_BOUNDARY,
            designSpaceBoundaries.rightX - itemWidth - SECTION_CHILD_PLACEMENT_BOUNDARY,
          ),
          y: clamp(
            screenPosition.y,
            SECTION_CHILD_PLACEMENT_BOUNDARY,
            sectionBoundingRect.height - itemHeight - SECTION_CHILD_PLACEMENT_BOUNDARY,
          ),
        };

        const updatedPosition = getElementPosition(designSpaceBoundaries.leftX, clampedScreenPosition);
        const updatedDimensions = { ...item.element.dimensions };

        if (elementStretch?.enabled) {
          updatedPosition.x = SECTION_CHILD_PLACEMENT_BOUNDARY;
          updatedDimensions.width = WEBSITE_BUILDER_DESIGN_PAGE_WIDTH - SECTION_CHILD_PLACEMENT_BOUNDARY * 2;
        }

        const updatedItem: TWebsiteBuilderElement = cloneDeep(item);

        updatedItem.element.position = updatedPosition;
        updatedItem.element.dimensions = updatedDimensions;
        updatedItem.element.zIndex = getSectionNextZIndex(node);

        dispatch(
          websiteBuilderAddNode({
            snapshot: {
              parentId: node.id,
              node: updatedItem,
              index: node.children.length,
            },
          }),
        );

        dispatch(
          websiteBuilderSelectNode({
            selectedNode: {
              parentId: node.id,
              id: updatedItem.id,
              editingMode: "none",
            },
          }),
        );
      },
      collect: (monitor): ISectionViewDropState => {
        return {
          isOver: monitor.isOver(),
        };
      },
    }),
    [node.id, node.children, designSpaceBoundaries.leftX, designSpaceBoundaries.rightX],
  );

  useEffect(() => {
    if (!dropState.isOver) {
      setDropItemBoundsCheck("none");
    }
  }, [dropState.isOver]);

  const sectionHighlight: TWebsiteBuilderSectionViewHighlight = useMemo(() => {
    const strongHighlight = sectionSelected || dropState.isOver;
    const weakHighlight = childSelected || sectionHovered;

    if (strongHighlight) {
      return "strong";
    } else if (weakHighlight) {
      return "weak";
    } else {
      return "none";
    }
  }, [sectionSelected, childSelected, dropState.isOver, sectionHovered]);

  const sectionPosition: TWebsiteBuilderSectionViewPosition = useMemo(() => {
    const outerSection = sectionType === "header" || sectionType === "footer";

    if (outerSection) {
      return {
        type: "outer",
      };
    } else {
      return {
        type: "inner",
        index: parentNode.children.findIndex(child => child.id === node.id),
      };
    }
  }, [sectionType, parentNode.children, node.id]);

  function getOutOfBoundsGradient(): Property.Background<string> {
    const outOfBoundsColor = "#EAECF0";

    if (dropItemBoundsCheck === "out-left") {
      return `linear-gradient(
          to right,
          ${outOfBoundsColor} ${designSpaceBoundaries.leftX}px,
          transparent ${designSpaceBoundaries.leftX}px
        )`;
    } else if (dropItemBoundsCheck === "out-right") {
      const rightXWithOffset = designSpaceBoundaries.rightX + 1;

      return `linear-gradient(to right,
          transparent ${rightXWithOffset}px,
          ${outOfBoundsColor} ${rightXWithOffset}px
        )`;
    } else {
      return "";
    }
  }

  return (
    <div
      ref={el => {
        drop(el);
        treeRefMapRegistration(el);
      }}
      className={classNames(WEBSITE_BUILDER_SECTION_CLASS_NAME, {
        "website-builder-section-weak-highlight": sectionHighlight === "weak",
        "website-builder-section-strong-highlight": sectionHighlight === "strong",
      })}
      style={{
        height: viewResizeHeight,
        background: getOutOfBoundsGradient(),
      }}
      onContextMenu={e => {
        e.preventDefault();

        handleViewOpenContextMenu({ x: e.clientX, y: e.clientY });
      }}
      onMouseOver={() => setSectionHovered(true)}
      onMouseOut={e => {
        const mouseOutToTarget = e.relatedTarget as HTMLElement | null;

        if (!mouseOutToTarget || !e.currentTarget.contains(mouseOutToTarget)) {
          setSectionHovered(false);
        }
      }}
      onPointerDown={e => {
        e.stopPropagation();

        if (!sectionSelected) {
          dispatch(
            websiteBuilderSelectNode({
              selectedNode: {
                parentId: parentNode.id,
                id: node.id,
                editingMode: "none",
              },
            }),
          );
        }

        if (e.button === 0 && sectionContextMenuActive) {
          dispatch(
            websiteBuilderUpdateSelectedNode({
              selectedNodeUpdates: {
                editingMode: "none",
              },
            }),
          );
        }
      }}
    >
      {children}
      <SectionHighlightTitle highlight={sectionHighlight} type={sectionType} />
      {!sectionResizing && (
        <SectionToolbar
          node={node}
          parentNode={parentNode}
          hovered={sectionHovered}
          position={sectionPosition}
          maxIndex={sectionMaxIndex}
          sectionOperations={sectionOperations}
          handleViewOpenContextMenu={handleViewOpenContextMenu}
          scrollContainerRef={scrollContainerRef}
          treeRefMap={treeRefMap}
        />
      )}
      <SectionAddSectionButtons
        parentId={parentNode.id}
        type={sectionType}
        position={sectionPosition}
        maxIndex={sectionMaxIndex}
        hovered={sectionHovered}
        scrollContainerRef={scrollContainerRef}
        treeRefMap={treeRefMap}
      />
      {viewResizeHandle}
      {viewContextMenu}
    </div>
  );
}
